import { Suspense } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';

import { AppShell } from '@/components/AppShell';
import { BRAND_KITS_UI_VISIBLE } from '@/features/brandKits/brandKitsGate';
import { LoginPage } from '@/pages/LoginPage';
// R316 — 公開頁(不包 RequireAuth):官網首頁 / 服務條款 / 免責聲明
import { LandingPage } from '@/pages/public/LandingPage';
import { UserTermsPage } from '@/pages/public/UserTermsPage';
import { DisclaimerPage } from '@/pages/public/DisclaimerPage';
import { HomePage } from '@/pages/HomePage';
import { DEFAULT_PROJECT_ID } from '@/features/canvas/datasets';
import { ProjectListPage } from '@/features/projects/ProjectListPage';

import { lazyWithReload } from './lazyWithReload';
import { adsRoutes } from './adsRoutes';
import { RequireAuth } from './RequireAuth';

// R106 — 合約制工作區開通(docs/65):owner 管成員 + platform-admin 建約(lazy code-split)
const OwnerContractPage = lazyWithReload('OwnerContractPage', () =>
  import('@/features/contracts/OwnerContractPage').then((m) => ({ default: m.OwnerContractPage })),
);
// R209 — 檢查報告頁(素材庫「檢查結果」badge / 列選單進入;A 案複合鍵路由)
const CheckReportPage = lazyWithReload('CheckReportPage', () =>
  import('@/features/assets/check/CheckReportPage').then((m) => ({ default: m.CheckReportPage })),
);
const AdminContractsPage = lazyWithReload('AdminContractsPage', () =>
  import('@/features/contracts/AdminContractsPage').then((m) => ({ default: m.AdminContractsPage })),
);
// R265 — 問題回報中心(入口 = /profile 右鍵選單 + 送出成功屏)/ 回報管理主控台(admin)。
// 兩支都是低頻頁 → lazy code-split,不進主 bundle。
const FeedbackCenterPage = lazyWithReload('FeedbackCenterPage', () =>
  import('@/features/feedback/FeedbackCenterPage').then((m) => ({ default: m.FeedbackCenterPage })),
);
const FeedbackAdminPage = lazyWithReload('FeedbackAdminPage', () =>
  import('@/features/feedback/admin/FeedbackAdminPage').then((m) => ({
    default: m.FeedbackAdminPage,
  })),
);
// R276 — 序號管理(admin;低頻頁 lazy code-split)。
const RedeemAdminPage = lazyWithReload('RedeemAdminPage', () =>
  import('@/features/redeem/admin/RedeemAdminPage').then((m) => ({
    default: m.RedeemAdminPage,
  })),
);
// R392 — 推薦分潤管理(admin;入口 = 帳號選單,role + referral flag 雙 gate)。
const ReferralAdminPage = lazyWithReload('ReferralAdminPage', () =>
  import('@/features/referral/admin/ReferralAdminPage').then((m) => ({
    default: m.ReferralAdminPage,
  })),
);
// R352 — 剪輯頁(專案底下的獨立頁,不 mount reactflow;owner 2026-09-10:入口刻意低調,
// 只有畫布空白處右鍵那一項)。低頻 + 自帶多 video 引擎 → lazy code-split,不進主 bundle。
const CutPage = lazyWithReload('CutPage', () =>
  import('@/features/cut/CutPage').then((m) => ({ default: m.CutPage })),
);

// R412(前端健檢)— 主 bundle 瘦身:畫布 / 素材庫 / 品牌規範 / 成員 / 設定 / 個人資料 / 說明改成
// route-level code-split(同既有 `lazyWithReload` + `LazyRoute`:跨部署 chunk 404 自癒一次)。
// ⚠️ 官網 / 條款 / 免責 / 登入 / 首頁 / 專案列表**刻意留在主 bundle**:前四支是未登入就要秒開的
// 公開頁,後兩支是登入後最常落地的頁 —— 多一次 chunk 等待不值得。
const CanvasPage = lazyWithReload('CanvasPage', () =>
  import('@/pages/CanvasPage').then((m) => ({ default: m.CanvasPage })),
);
const BrandKitListPage = lazyWithReload('BrandKitListPage', () =>
  import('@/features/brandKits/BrandKitListPage').then((m) => ({ default: m.BrandKitListPage })),
);
const BrandKitEditPage = lazyWithReload('BrandKitEditPage', () =>
  import('@/features/brandKits/BrandKitEditPage').then((m) => ({ default: m.BrandKitEditPage })),
);
const GroupMembersPage = lazyWithReload('GroupMembersPage', () =>
  import('@/features/groups/GroupMembersPage').then((m) => ({ default: m.GroupMembersPage })),
);
const AssetLibraryPage = lazyWithReload('AssetLibraryPage', () =>
  import('@/features/assets/AssetLibraryPage').then((m) => ({ default: m.AssetLibraryPage })),
);
const AssetLibrariesOverviewPage = lazyWithReload('AssetLibrariesOverviewPage', () =>
  import('@/features/assets/AssetLibrariesOverviewPage').then((m) => ({ default: m.AssetLibrariesOverviewPage })),
);
const AdsAssetsLibraryPage = lazyWithReload('AdsAssetsLibraryPage', () =>
  import('@/features/assets/AdsAssetsLibraryPage').then((m) => ({ default: m.AdsAssetsLibraryPage })),
);
const WorkspaceSettingsPage = lazyWithReload('WorkspaceSettingsPage', () =>
  import('@/features/workspace/WorkspaceSettingsPage').then((m) => ({ default: m.WorkspaceSettingsPage })),
);
const UserProfilePage = lazyWithReload('UserProfilePage', () =>
  import('@/features/profile/UserProfilePage').then((m) => ({ default: m.UserProfilePage })),
);
const HelpPage = lazyWithReload('HelpPage', () =>
  import('@/features/help/HelpPage').then((m) => ({ default: m.HelpPage })),
);

/**
 * lazy page 共用守衛 + Suspense fallback(對齊 adsRoutes `AdsRouteWrapper`)。
 *
 * R412 — fallback = **空的 AppShell**(側欄照常、內容區留白),不是整頁空白:這裡的頁面
 * 全都包在 AppShell 裡,整頁空白的 fallback 會讓側欄在 chunk 下載那一下消失再出現
 * (畫布 / 素材庫是高頻入口,首次進入就閃一次)。AppShell 已在主 bundle(首頁用),零成本。
 */
function LazyRoute({ children }: { children: React.ReactNode }) {
  return (
    <RequireAuth>
      <Suspense
        fallback={
          <AppShell mainPadding={0}>
            <div data-testid="lazy-route-fallback" />
          </AppShell>
        }
      >
        {children}
      </Suspense>
    </RequireAuth>
  );
}

/**
 * 路由定義(R164 大改版收斂 — D3 2026-07-29 六域:首頁 / 專案 / 素材庫 /
 * 成效報表 / 品牌規範 / 廣告串接)
 * - ``/``:自動導向 ``/home``。
 * - ``/login``:登入頁(未登入時的著陸點)。
 * - ``/home``:首頁;``/projects``:專案列表;``/canvas/:projectId``:專案工作區(畫布)。
 * - ``/assets``:素材庫;``/brand-kits``:品牌規範;``/ads/*``:廣告串接 + 成效報表。
 * - ``/new``:專案列表 + 建立專案燈箱(R293;完成後直進畫布)。
 * - 營運底盤(側邊欄外入口):``/workspace/settings``(工作區切換器)/
 *   ``/profile`` ``/help``(帳號選單)/ ``/groups/:gid/members`` ``/contracts/mine``
 *   ``/admin/contracts``(合約制)/ ``/feedback`` ``/admin/feedback``(問題回報,
 *   入口 = 個人資料頁右鍵選單)。
 *
 * R164 刪除(D3 新版不存在):/new/brand-kit + /new/project(legacy 兩段 wizard)、
 * /projects/:pid/sources(靈感來源)、/projects/:pid/insights(AI 洞察)、
 * /projects/:pid/text-gen(文字生成頁;文案 = 畫布文案卡)、/abcd/*(ABCD 評分)、
 * /projects/:pid/subtitle/:assetId(上字編輯器)。
 */
/** 路由表本體(R412 抽出:測試用 memory router 掛同一份,`router` 只是把它交給 browser router)。 */
export const appRoutes: RouteObject[] = [
  // R316 — `/` 是**官網首頁**,不再無條件轉去 /home(owner 2026-09-11 拍板)。
  // ⚠️ 刻意**沒有**「已登入就自動轉址」:已登入的人進 `/` 一樣看到官網,由頁面上那顆
  // 「立即啟用」進 /home。加了自動轉址就等於已登入的人再也看不到官網。
  { path: '/', element: <LandingPage /> },
  // 公開法律頁 — 路徑與 mentor 一致(mentor-login.haitech.com.tw/user-terms、/disclaimer)。
  // ⚠️ 這兩條**不可以**包 RequireAuth:條款與免責聲明必須在登入前就讀得到。
  { path: '/user-terms', element: <UserTermsPage /> },
  { path: '/disclaimer', element: <DisclaimerPage /> },
  { path: '/login', element: <LoginPage /> },
  {
    path: '/home',
    element: (
      <RequireAuth>
        <HomePage />
      </RequireAuth>
    ),
  },
  {
    path: '/canvas',
    element: <Navigate to={`/canvas/${DEFAULT_PROJECT_ID}`} replace />,
  },
  {
    path: '/canvas/:projectId',
    element: (
      <LazyRoute>
        <CanvasPage />
      </LazyRoute>
    ),
  },
  // R352 — 剪輯頁:住在專案脈絡底下(同麵包屑、返回畫布),但**不是綠卡**、不 mount
  // reactflow(owner 2026-09-09 拍板)。時間軸只落本機草稿,BE `edits` 落地後再接。
  {
    path: '/canvas/:projectId/cut',
    element: (
      <LazyRoute>
        <CutPage />
      </LazyRoute>
    ),
  },
  // 建立專案。R293 起表單是**專案列表上的燈箱**(D3 `openForm('create')` → `.pj-modal`),
  // 不是獨立頁面;`/new` 保留成「開著燈箱的專案列表」,讓首頁 CTA 與既有深連結照走。
  // 完成後直進 `/canvas/{pid}`(R164 起;靈感來源頁已刪)。
  {
    path: '/new',
    element: (
      <RequireAuth>
        <ProjectListPage autoOpenCreate />
      </RequireAuth>
    ),
  },
  // R18:BrandKit 管理頁(側邊欄「品牌規範」)
  // R397 — 品牌規範 UI 隱藏(owner 拍板,授權才開;gate 單點 brandKitsGate):
  // gate 關時深連結一律回專案列表 —— 頁面本體與入口一起藏,不留「入口沒了但網址還開得進」的後門。
  {
    path: '/brand-kits',
    element: BRAND_KITS_UI_VISIBLE ? (
      <LazyRoute>
        <BrandKitListPage />
      </LazyRoute>
    ) : (
      <Navigate to="/projects" replace />
    ),
  },
  {
    path: '/brand-kits/:brandKitId',
    element: BRAND_KITS_UI_VISIBLE ? (
      <LazyRoute>
        <BrandKitEditPage />
      </LazyRoute>
    ) : (
      <Navigate to="/projects" replace />
    ),
  },
  // R20:專案列表(側邊欄「專案」)
  {
    path: '/projects',
    element: (
      <RequireAuth>
        <ProjectListPage />
      </RequireAuth>
    ),
  },
  // R292.11(owner 2026-08-30 拍板)— **`/assets` 直接進素材清單**(對齊 D3:側欄
  // 「素材庫」點下去就是平台素材,不再先過一層「有哪些庫」)。
  // R240 的總覽頁**封存不刪**:route 保留在 `/assets/libraries`(側欄不再指它,
  // 只剩深連結進得去)。owner:「暫時不刪除,兩個月後如果還在可以刪除頁碼,
  // 但存放方式照舊」→ **2026-10-30 之後可移除這個 route 與頁面元件**,
  // 資料面(`asset_libraries` 多庫結構)不動。
  {
    path: '/assets',
    element: (
      <LazyRoute>
        <AssetLibraryPage />
      </LazyRoute>
    ),
  },
  // 舊網址相容:`/assets/library` 仍指清單(既有連結/書籤不斷)。
  {
    path: '/assets/library',
    element: (
      <LazyRoute>
        <AssetLibraryPage />
      </LazyRoute>
    ),
  },
  // 🗄 封存(R292.11;2026-10-30 後可刪)— 素材庫總覽「有哪些庫」。
  {
    path: '/assets/libraries',
    element: (
      <LazyRoute>
        <AssetLibrariesOverviewPage />
      </LazyRoute>
    ),
  },
  // R240:廣告素材分區(隱藏庫 `ads-lib-{gid}`,BE 同步完成自動入庫)
  {
    path: '/assets/ads',
    element: (
      <LazyRoute>
        <AdsAssetsLibraryPage />
      </LazyRoute>
    ),
  },
  // R209:檢查報告頁(確認清單 B 節;素材庫 item 複合鍵 — H 節全域 id 拍板前走 A 案)
  {
    path: '/assets/check-report/:libId/:itemId',
    element: (
      <LazyRoute>
        <CheckReportPage />
      </LazyRoute>
    ),
  },
  // R42 — 廣告串接 + 成效報表:`/ads/{connect,connecting,accounts/select,accounts,sync,reports}`
  ...adsRoutes,
  // R46 — Beta v0.0.1 Group members management(owner-only invite/remove,member 可讀)
  {
    path: '/groups/:gid/members',
    element: (
      <LazyRoute>
        <GroupMembersPage />
      </LazyRoute>
    ),
  },
  // R106 — 合約制(docs/65):owner 自助管成員 + platform-admin 建約
  {
    path: '/contracts/mine',
    element: (
      <LazyRoute>
        <OwnerContractPage />
      </LazyRoute>
    ),
  },
  {
    path: '/admin/contracts',
    element: (
      <LazyRoute>
        <AdminContractsPage />
      </LazyRoute>
    ),
  },
  // R265 — 問題回報(入口刻意低調:`/profile` 右鍵選單 + 回報送出成功那一屏)
  {
    path: '/feedback',
    element: (
      <LazyRoute>
        <FeedbackCenterPage />
      </LazyRoute>
    ),
  },
  {
    path: '/admin/feedback',
    element: (
      <LazyRoute>
        <FeedbackAdminPage />
      </LazyRoute>
    ),
  },
  // R276 — 序號兌換點數:admin 產碼 + 歷史清單(入口 = 帳號選單,role gate)。
  {
    path: '/admin/redeem-codes',
    element: (
      <LazyRoute>
        <RedeemAdminPage />
      </LazyRoute>
    ),
  },
  // R392 — 推薦碼 × 業務歸屬 × 月結分潤(admin 主控台;docs/128)。
  {
    path: '/admin/referral',
    element: (
      <LazyRoute>
        <ReferralAdminPage />
      </LazyRoute>
    ),
  },
  // 工作區設定(入口:側邊欄工作區切換器 dropdown)/ 個人資料 / 說明(入口:帳號選單)
  {
    path: '/workspace/settings',
    element: (
      <LazyRoute>
        <WorkspaceSettingsPage />
      </LazyRoute>
    ),
  },
  {
    path: '/profile',
    element: (
      <LazyRoute>
        <UserProfilePage />
      </LazyRoute>
    ),
  },
  {
    path: '/help',
    element: (
      <LazyRoute>
        <HelpPage />
      </LazyRoute>
    ),
  },
  { path: '*', element: <Navigate to="/home" replace /> },
];

export const router = createBrowserRouter(appRoutes);
